import { useState, useEffect, useCallback, useContext, createContext } from 'react';
import api from '../utils/api';
import { useToast } from './useToast';

// Create a context for authentication
const AuthContext = createContext();

/**
 * Provider component for the authentication context
 * Manages authentication state and provides methods for login, logout, etc.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const { showToast } = useToast();
  
  // Load user data on initial mount
  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setUser(null);
        setLoading(false);
        setIsInitialized(true);
        return;
      }
      
      // Set auth token in API headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Fetch current user profile
      const res = await api.get('/auth/me');
      
      if (res.data && res.data.success) {
        const userData = res.data.data || res.data.user;
        
        // Ensure profile image is properly set with consistency
        if (userData) {
          // If profileImage doesn't exist but profilePicture does, use that instead
          if (!userData.profileImage && userData.profilePicture) {
            userData.profileImage = userData.profilePicture;
          }
          // If profilePicture doesn't exist but profileImage does, use that instead
          if (!userData.profilePicture && userData.profileImage) {
            userData.profilePicture = userData.profileImage;
          }
        }
        
        setUser(userData);
      } else {
        throw new Error('Invalid user data response');
      }
    } catch (error) {
      console.error('Load user error:', error);
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, []);
  
  // Initial loading of user data
  useEffect(() => {
    loadUser();
  }, [loadUser]);
  
  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Login result
   */
  const login = async (email, password) => {
    try {
      setLoading(true);
      const res = await api.post('/auth/login', { email, password });
      
      // Handle different response formats
      let token, user;
      if (res.data.data) {
        token = res.data.data.token;
        user = res.data.data.user;
      } else {
        token = res.data.token;
        user = res.data.user;
      }
      
      if (!token || !user) {
        throw new Error('Invalid login response format');
      }
      
      // Ensure profile image is properly set with consistency
      if (user) {
        if (!user.profileImage && user.profilePicture) {
          user.profileImage = user.profilePicture;
        }
        if (!user.profilePicture && user.profileImage) {
          user.profilePicture = user.profileImage;
        }
      }
      
      // Save token and set in headers
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(user);
      showToast('Login successful!', 'success');
      return { success: true, user };
    } catch (error) {
      console.error('Login error:', error);
      showToast(error.response?.data?.message || 'Login failed', 'error');
      return { success: false, error: error.response?.data?.message || 'Login failed' };
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} Registration result
   */
  const register = async (userData) => {
    try {
      setLoading(true);
      // Ensure userData has a role or default to 'parent'
      const dataToSend = {
        ...userData,
        role: userData.role || 'parent'
      };
      
      // Add phoneNumber if missing (required by the backend)
      if (!dataToSend.phoneNumber) {
        dataToSend.phoneNumber = dataToSend.email; // Use email as temporary value
      }
      
      // Ensure subscription object is properly structured
      if (!dataToSend.subscription) {
        dataToSend.subscription = {
          tier: 'free',
          startDate: new Date().toISOString(),
          endDate: null,
          isActive: true
        };
      }
      
      // Remove the unconditional overwrite of subscription
      // Debug the data being sent
      console.log('Registration data being sent:', JSON.stringify(dataToSend));
      
      const res = await api.post('/auth/register', dataToSend);
      
      // Log successful response
      console.log('Registration response:', res.data);
      
      // Handle different response formats
      let token, user;
      if (res.data.data) {
        token = res.data.data.token;
        user = res.data.data.user;
      } else {
        token = res.data.token;
        user = res.data.user;
      }
      
      if (!token || !user) {
        throw new Error('Invalid registration response format');
      }
      
      // Ensure profile image is properly set with consistency
      if (user) {
        if (!user.profileImage && user.profilePicture) {
          user.profileImage = user.profilePicture;
        }
        if (!user.profilePicture && user.profileImage) {
          user.profilePicture = user.profileImage;
        }
      }
      
      // Save token and set in headers
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(user);
      showToast('Registration successful!', 'success');
      return { success: true, user };
    } catch (error) {
      console.error('Register error:', error);
      const errorMessage = error.response?.data?.message || 'Registration failed';
      showToast(errorMessage, 'error');
      
      // Return a structured error response
      return { 
        success: false, 
        error: errorMessage,
        details: error.response?.data?.errors || []
      };
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Log out the current user
   */
  const logout = useCallback(async () => {
    try {
      // Call logout endpoint to invalidate token on server
      await api.get('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Remove token regardless of server response
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      showToast('Logged out successfully', 'info');
    }
  }, [showToast]);
  
  /**
   * Refresh user data from the server
   */
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      const userData = res.data.data;
      
      // Ensure profile image is properly set with consistency
      if (userData) {
        // If profileImage doesn't exist but profilePicture does, use that instead
        if (!userData.profileImage && userData.profilePicture) {
          userData.profileImage = userData.profilePicture;
        }
        // If profilePicture doesn't exist but profileImage does, use that instead
        if (!userData.profilePicture && userData.profileImage) {
          userData.profilePicture = userData.profileImage;
        }
      }
      
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Refresh user error:', error);
      if (error.response?.status === 401) {
        // Handle unauthorized error (token expired or invalid)
        logout();
      }
      return null;
    }
  }, [logout]);
  
  /**
   * Send password reset email
   * @param {string} email - User email
   * @returns {Promise} Result of the operation
   */
  const forgotPassword = async (email) => {
    try {
      await api.post('/auth/forgotpassword', { email });
      showToast('Password reset instructions sent to your email', 'success');
      return { success: true };
    } catch (error) {
      console.error('Forgot password error:', error);
      showToast(error.response?.data?.message || 'Failed to send reset email', 'error');
      return { success: false, error: error.response?.data?.message };
    }
  };
  
  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} password - New password
   * @returns {Promise} Result of the operation
   */
  const resetPassword = async (token, password) => {
    try {
      await api.put(`/auth/resetpassword/${token}`, { password });
      showToast('Password reset successful. You can now log in with your new password.', 'success');
      return { success: true };
    } catch (error) {
      console.error('Reset password error:', error);
      showToast(error.response?.data?.message || 'Failed to reset password', 'error');
      return { success: false, error: error.response?.data?.message };
    }
  };
  
  /**
   * Update user's password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} Result of the operation
   */
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/auth/updatepassword', { 
        currentPassword, 
        newPassword 
      });
      showToast('Password updated successfully', 'success');
      return { success: true };
    } catch (error) {
      console.error('Update password error:', error);
      showToast(error.response?.data?.message || 'Failed to update password', 'error');
      return { success: false, error: error.response?.data?.message };
    }
  };
  
  /**
   * Update user profile
   * @param {Object} profileData - Updated profile data
   * @returns {Promise} Result of the operation
   */
  const updateProfile = async (profileData) => {
    try {
      const res = await api.put('/auth/updatedetails', profileData);
      
      const userData = res.data.data || res.data.user;
      if (userData) {
        // Ensure profile image is properly set with consistency
        if (!userData.profileImage && userData.profilePicture) {
          userData.profileImage = userData.profilePicture;
        }
        if (!userData.profilePicture && userData.profileImage) {
          userData.profilePicture = userData.profileImage;
        }
        
        setUser(userData);
        showToast('Profile updated successfully', 'success');
        return { success: true, user: userData };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      showToast(error.response?.data?.message || 'Failed to update profile', 'error');
      return { success: false, error: error.response?.data?.message };
    }
  };
  
  // Provide auth context value
  const updateUserData = useCallback((userData) => {
    // Ensure profile image is properly set with consistency
    if (userData) {
      // If profileImage doesn't exist but profilePicture does, use that instead
      if (!userData.profileImage && userData.profilePicture) {
        userData.profileImage = userData.profilePicture;
      }
      // If profilePicture doesn't exist but profileImage does, use that instead
      if (!userData.profilePicture && userData.profileImage) {
        userData.profilePicture = userData.profileImage;
      }
    }
    
    setUser(userData);
    return userData;
  }, []);

  // Provide auth context value
  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isInitialized,
    login,
    register,
    logout,
    refreshUser,
    forgotPassword,
    resetPassword,
    updatePassword,
    updateProfile,
    updateUserData
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the authentication context
 * @returns {Object} Authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default useAuth;